import { useEffect } from 'react';
import type { ThreeCtx } from './useThreeScene';
import type { EffectsCtl } from './useEffects';
import { isFacadeGestureAnimating } from '../facadeDirectManipulation';

export type LoopHook = (delta: number) => boolean | void;

export function boundedActiveFrameDelta(rawDelta: number): number {
  return Math.min(Math.max(rawDelta, 0), 0.25);
}

export function discardIdleSleepBeforeRaf(clock: { getDelta: () => number }): void {
  clock.getDelta();
}

export function useRenderLoop(
  ctx: ThreeCtx | null,
  hooks: LoopHook[],
  effects?: React.MutableRefObject<EffectsCtl | null>,
  paused?: boolean,
) {
  useEffect(() => {
    if (!ctx) return;
    let frameId = 0;
    let timeoutId = 0;
    let stopped = false;
    let scheduled = false;
    let idleFrames = 0;

    const schedule = (fast: boolean) => {
      if (stopped || scheduled) return;
      scheduled = true;
      if (fast) {
        frameId = requestAnimationFrame(loop);
        return;
      }
      timeoutId = window.setTimeout(() => {
        timeoutId = 0;
        // Discard the intentional idle sleep before measuring the next RAF.
        // Otherwise adaptive quality sees the 500 ms scheduler pause as a
        // 2 FPS render frame and permanently degrades a static scene.
        discardIdleSleepBeforeRaf(ctx.clock);
        frameId = requestAnimationFrame(loop);
      }, 500);
    };

    const wake = () => {
      idleFrames = 0;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = 0;
        scheduled = false;
        // Input cancelled the idle timeout, so that callback cannot discard
        // its intentional sleep. Reset here before the immediate RAF instead.
        discardIdleSleepBeforeRaf(ctx.clock);
      }
      schedule(true);
    };

    const loop = () => {
      if (stopped) return;
      scheduled = false;
      frameId = 0;
      // Preserve real active-frame timings below 30 FPS. The previous 1/30
      // clamp converted every slow frame into exactly 30 FPS, masking load.
      const delta = boundedActiveFrameDelta(ctx.clock.getDelta());
      let hookBusy = false;
      for (const fn of hooks) {
        if (fn(delta) === true) hookBusy = true;
      }
      // WALKTHROUGH: OrbitControls.update() безусловно lookAt(target) —
      // в первом лице он каждый кадр отбирал бы камеру. Режим держит её сам.
      const controlsBusy = ctx.walkthroughActive ? false : ctx.controls.update() === true;
      // Без controls.update() никто не сигналит busy, пока идёт прогулка:
      // idle-детектор заглушал бы рендер до 500мс-тика, WASD — слайдшоу 2fps.
      const busy = hookBusy || controlsBusy || ctx.walkthroughActive;
      idleFrames = busy ? 0 : idleFrames + 1;

      // Hot path while the user interacts, slow idle loop once the viewer is static.
      const slowIdle = idleFrames > 45;
      const shouldRender = busy || slowIdle || idleFrames < 6 || idleFrames % 4 === 0;
      if (!paused) {
        if (shouldRender) {
          if (effects?.current) effects.current.render();
          else ctx.renderer.render(ctx.scene, ctx.camera);
        }
      }
      schedule(!slowIdle || busy);
    };
    ctx.renderer.domElement.addEventListener('pointerdown', wake, { passive: true });
    ctx.renderer.domElement.addEventListener('wheel', wake, { passive: true });
    /* W3 — прямая манипуляция фасадами: палец тащит фасад pointermove'ами,
       но сам по себе pointermove цикл не будит. Пока жив жест (или его
       доводчик), движение указателя обязано немедленно просыпаться — иначе
       после секундной заминки в середине драга фасад отстанет от пальца до
       полсекунды. Вне жеста pointermove НЕ будит цикл (холостой ход мыши
       не должен греть рендер). */
    const wakeForFacadeGesture = () => {
      if (isFacadeGestureAnimating()) wake();
    };
    ctx.renderer.domElement.addEventListener('pointermove', wakeForFacadeGesture, { passive: true });
    ctx.controls.addEventListener('start', wake);
    ctx.controls.addEventListener('change', wake);
    schedule(true);
    return () => {
      stopped = true;
      if (frameId) cancelAnimationFrame(frameId);
      if (timeoutId) window.clearTimeout(timeoutId);
      ctx.renderer.domElement.removeEventListener('pointerdown', wake);
      ctx.renderer.domElement.removeEventListener('wheel', wake);
      ctx.renderer.domElement.removeEventListener('pointermove', wakeForFacadeGesture);
      ctx.controls.removeEventListener('start', wake);
      ctx.controls.removeEventListener('change', wake);
    };
  }, [ctx, hooks, effects, paused]);
}
